import { storableError } from '../../util/errors';
import { adminListWithdrawals, adminApproveWithdrawal, adminRejectWithdrawal, adminConfirmWithdrawal } from '../../util/api';
import { parse } from '../../util/urlHelpers';

// ================ Action types ================ //
export const FETCH_WITHDRAWALS_REQUEST = 'app/AdminWithdrawalsPage/FETCH_WITHDRAWALS_REQUEST';
export const FETCH_WITHDRAWALS_SUCCESS = 'app/AdminWithdrawalsPage/FETCH_WITHDRAWALS_SUCCESS';
export const FETCH_WITHDRAWALS_ERROR = 'app/AdminWithdrawalsPage/FETCH_WITHDRAWALS_ERROR';

export const FETCH_COUNTS_REQUEST = 'app/AdminWithdrawalsPage/FETCH_COUNTS_REQUEST';
export const FETCH_COUNTS_SUCCESS = 'app/AdminWithdrawalsPage/FETCH_COUNTS_SUCCESS';
export const FETCH_COUNTS_ERROR = 'app/AdminWithdrawalsPage/FETCH_COUNTS_ERROR';

export const APPROVE_WITHDRAWAL_REQUEST = 'app/AdminWithdrawalsPage/APPROVE_WITHDRAWAL_REQUEST';
export const APPROVE_WITHDRAWAL_SUCCESS = 'app/AdminWithdrawalsPage/APPROVE_WITHDRAWAL_SUCCESS';
export const APPROVE_WITHDRAWAL_ERROR = 'app/AdminWithdrawalsPage/APPROVE_WITHDRAWAL_ERROR';

export const REJECT_WITHDRAWAL_REQUEST = 'app/AdminWithdrawalsPage/REJECT_WITHDRAWAL_REQUEST';
export const REJECT_WITHDRAWAL_SUCCESS = 'app/AdminWithdrawalsPage/REJECT_WITHDRAWAL_SUCCESS';
export const REJECT_WITHDRAWAL_ERROR = 'app/AdminWithdrawalsPage/REJECT_WITHDRAWAL_ERROR';

export const CONFIRM_WITHDRAWAL_REQUEST = 'app/AdminWithdrawalsPage/CONFIRM_WITHDRAWAL_REQUEST';
export const CONFIRM_WITHDRAWAL_SUCCESS = 'app/AdminWithdrawalsPage/CONFIRM_WITHDRAWAL_SUCCESS';
export const CONFIRM_WITHDRAWAL_ERROR = 'app/AdminWithdrawalsPage/CONFIRM_WITHDRAWAL_ERROR';

// ================ Reducer ================ //
const emptyPage = { items: [], pagination: { total: 0, page: 1, perPage: 10 } };

export const confirmWithdrawal = (withdrawalId) => async (dispatch, getState) => {
  dispatch(confirmWithdrawalRequest(withdrawalId));

  try {
    const currentUser = getState()?.user?.currentUser;
    const currentUserId = currentUser?.id?.uuid;

    if (!currentUserId) {
      throw new Error('User not authenticated');
    }

    const res = await adminConfirmWithdrawal({
      requestId: withdrawalId,
      confirmedBy: currentUserId,
      currentUserId,
    });
    const data = res?.data?.data || res?.data;
    const updated = data?.request || data?.item || data;
    dispatch(confirmWithdrawalSuccess(updated));
  } catch (error) {
    console.error('Error confirming withdrawal:', error);
    const err = storableError(error);
    dispatch(confirmWithdrawalError(err));
  }
};
const initialState = {
  // Back-compat flat fields (optional to use)
  withdrawals: [],
  pagination: { total: 0, page: 1, perPage: 10 },
  // Per-status caches to avoid tab data mixups
  byStatus: {
    all: { ...emptyPage },
    pending: { ...emptyPage },
    approved: { ...emptyPage },
    rejected: { ...emptyPage },
  },
  counts: { all: 0, pending: 0, approved: 0, rejected: 0 },
  loading: false,
  mutatingId: null,
  mutatingAction: null,
  countsLoading: false,
  error: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_WITHDRAWALS_REQUEST:
      return { ...state, loading: true, error: null };

    case FETCH_WITHDRAWALS_SUCCESS: {
      const items = payload.items || payload.withdrawals || [];
      const pagination = payload.pagination || state.pagination;
      const counts = payload.counts || state.counts;
      const status = payload.status; // can be undefined for 'all'
      const key = status || 'all';
      return {
        ...state,
        loading: false,
        withdrawals: items,
        pagination,
        counts,
        byStatus: {
          ...state.byStatus,
          [key]: { items, pagination },
        },
        error: null,
      };
    }

    case FETCH_WITHDRAWALS_ERROR:
      return { ...state, loading: false, error: payload };

    case APPROVE_WITHDRAWAL_REQUEST:
    case REJECT_WITHDRAWAL_REQUEST:
    case CONFIRM_WITHDRAWAL_REQUEST:
      return { ...state, mutatingId: payload?.id || null, mutatingAction: payload?.action || null, error: null };

    case APPROVE_WITHDRAWAL_SUCCESS:
    case REJECT_WITHDRAWAL_SUCCESS:
    case CONFIRM_WITHDRAWAL_SUCCESS: {
      const updated = payload?.item;
      if (!updated || !updated._id) return { ...state, mutatingId: null, error: null };
      const id = String(updated._id);

      const toKey = s => (s === 'approved' ? 'approved' : s === 'rejected' ? 'rejected' : 'pending');
      const newKey = toKey(updated.status);

      const findPrevKey = () => {
        const keys = ['pending', 'approved', 'rejected'];
        for (let k of keys) {
          const arr = state.byStatus[k]?.items || [];
          if (arr.some(it => String(it._id) === id)) return k;
        }
        return null;
      };
      const prevKey = findPrevKey();

      const belongsTo = (bucketKey) => {
        if (bucketKey === 'all') return true;
        return bucketKey === newKey;
      };

      const updateArray = (arr, bucketKey) => {
        const idx = (arr || []).findIndex(it => String(it._id) === id);
        if (idx === -1) {
          return belongsTo(bucketKey) ? [updated, ...(arr || [])] : (arr || []);
        }
        if (!belongsTo(bucketKey)) {
          return arr.filter(it => String(it._id) !== id);
        }
        const copy = arr.slice();
        copy[idx] = { ...copy[idx], ...updated };
        return copy;
      };

      const nextByStatus = {
        ...state.byStatus,
        all: {
          ...state.byStatus.all,
          items: updateArray(state.byStatus.all.items || [], 'all'),
          pagination: state.byStatus.all.pagination,
        },
        pending: {
          ...state.byStatus.pending,
          items: updateArray(state.byStatus.pending.items || [], 'pending'),
          pagination: state.byStatus.pending.pagination,
        },
        approved: {
          ...state.byStatus.approved,
          items: updateArray(state.byStatus.approved.items || [], 'approved'),
          pagination: state.byStatus.approved.pagination,
        },
        rejected: {
          ...state.byStatus.rejected,
          items: updateArray(state.byStatus.rejected.items || [], 'rejected'),
          pagination: state.byStatus.rejected.pagination,
        },
      };

      let nextCounts = { ...state.counts };
      if (prevKey && prevKey !== newKey) {
        if (prevKey === 'pending') nextCounts.pending = Math.max(0, (nextCounts.pending || 0) - 1);
        if (prevKey === 'approved') nextCounts.approved = Math.max(0, (nextCounts.approved || 0) - 1);
        if (prevKey === 'rejected') nextCounts.rejected = Math.max(0, (nextCounts.rejected || 0) - 1);
        if (newKey === 'pending') nextCounts.pending = (nextCounts.pending || 0) + 1;
        if (newKey === 'approved') nextCounts.approved = (nextCounts.approved || 0) + 1;
        if (newKey === 'rejected') nextCounts.rejected = (nextCounts.rejected || 0) + 1;
      }

      return { ...state, mutatingId: null, mutatingAction: null, byStatus: nextByStatus, counts: nextCounts, error: null };
    }

    case APPROVE_WITHDRAWAL_ERROR:
    case REJECT_WITHDRAWAL_ERROR:
    case CONFIRM_WITHDRAWAL_ERROR:
      return { ...state, loading: false, mutatingId: null, mutatingAction: null, error: payload };

    case FETCH_COUNTS_REQUEST:
      return { ...state, countsLoading: true };
    
    case FETCH_COUNTS_SUCCESS:
      return { ...state, countsLoading: false, counts: payload };
    
    case FETCH_COUNTS_ERROR:
      return { ...state, countsLoading: false };

    default:
      return state;
  }
}

// ================ Action creators ================ //
export const fetchWithdrawalsRequest = () => ({ type: FETCH_WITHDRAWALS_REQUEST });
export const fetchWithdrawalsSuccess = (payload) => ({ type: FETCH_WITHDRAWALS_SUCCESS, payload });
export const fetchWithdrawalsError = (error) => ({ type: FETCH_WITHDRAWALS_ERROR, payload: error });

export const approveWithdrawalRequest = (id) => ({ type: APPROVE_WITHDRAWAL_REQUEST, payload: { id, action: 'approve' } });
export const approveWithdrawalSuccess = (item) => ({ type: APPROVE_WITHDRAWAL_SUCCESS, payload: { item } });
export const approveWithdrawalError = (error) => ({ type: APPROVE_WITHDRAWAL_ERROR, payload: error });

export const rejectWithdrawalRequest = (id) => ({ type: REJECT_WITHDRAWAL_REQUEST, payload: { id, action: 'reject' } });
export const rejectWithdrawalSuccess = (item) => ({ type: REJECT_WITHDRAWAL_SUCCESS, payload: { item } });
export const rejectWithdrawalError = (error) => ({ type: REJECT_WITHDRAWAL_ERROR, payload: error });

export const confirmWithdrawalRequest = (id) => ({ type: CONFIRM_WITHDRAWAL_REQUEST, payload: { id, action: 'confirm' } });
export const confirmWithdrawalSuccess = (item) => ({ type: CONFIRM_WITHDRAWAL_SUCCESS, payload: { item } });
export const confirmWithdrawalError = (error) => ({ type: CONFIRM_WITHDRAWAL_ERROR, payload: error });

export const fetchCountsRequest = () => ({ type: FETCH_COUNTS_REQUEST });
export const fetchCountsSuccess = (counts) => ({ type: FETCH_COUNTS_SUCCESS, payload: counts });
export const fetchCountsError = (error) => ({ type: FETCH_COUNTS_ERROR, payload: error });

// ================ Thunks ================ //
export const fetchWithdrawals = ({ page = 1, perPage = 10, status } = {}) => async (dispatch, getState) => {
  dispatch(fetchWithdrawalsRequest());

  try {
    const currentUser = getState()?.user?.currentUser;
    const currentUserId = currentUser?.id?.uuid;

    if (!currentUserId) {
      throw new Error('User not authenticated');
    }

    // Fetch withdrawals with pagination

    const response = await adminListWithdrawals({
      page,
      perPage,
      status,
      currentUserId
    });

    const data = response?.data?.data || response?.data;
    const withdrawals = data?.items || [];
    const pagination = data?.pagination || { total: 0, page, perPage };
    const counts = data?.counts;

    dispatch(fetchWithdrawalsSuccess({ items: withdrawals, pagination, counts, status }));
  } catch (error) {
    console.error('Error fetching withdrawals:', error);
    const err = storableError(error);
    dispatch(fetchWithdrawalsError(err));
  }
};

export const fetchCounts = () => async (dispatch, getState) => {
  dispatch(fetchCountsRequest());
  
  try {
    const currentUser = getState()?.user?.currentUser;
    const currentUserId = currentUser?.id?.uuid;
    
    if (!currentUserId) {
      throw new Error('User not authenticated');
    }

    // Fetch counts for all statuses
    const [allRes, pendingRes, approvedRes, rejectedRes] = await Promise.all([
      adminListWithdrawals({ page: 1, perPage: 1, currentUserId }),
      adminListWithdrawals({ page: 1, perPage: 1, status: 'pending', currentUserId }),
      adminListWithdrawals({ page: 1, perPage: 1, status: 'approved', currentUserId }),
      adminListWithdrawals({ page: 1, perPage: 1, status: 'rejected', currentUserId }),
    ]);

    const getTotal = (response) => {
      const data = response?.data?.data || response?.data;
      return data?.pagination?.total || 0;
    };

    const counts = {
      all: getTotal(allRes),
      pending: getTotal(pendingRes),
      approved: getTotal(approvedRes),
      rejected: getTotal(rejectedRes),
    };

    dispatch(fetchCountsSuccess(counts));
  } catch (error) {
    console.error('Error fetching counts:', error);
    const err = storableError(error);
    dispatch(fetchCountsError(err));
  }
};

export const approveWithdrawal = (withdrawalId) => async (dispatch, getState) => {
  dispatch(approveWithdrawalRequest(withdrawalId));

  try {
    const currentUser = getState()?.user?.currentUser;
    const currentUserId = currentUser?.id?.uuid;

    if (!currentUserId) {
      throw new Error('User not authenticated');
    }



    const res = await adminApproveWithdrawal({
      requestId: withdrawalId,
      reviewedBy: currentUserId,
      currentUserId
    });
    const data = res?.data?.data || res?.data;
    const updated = data?.request || data?.item || data;
    dispatch(approveWithdrawalSuccess(updated));
  } catch (error) {
    console.error('Error approving withdrawal:', error);
    const err = storableError(error);
    dispatch(approveWithdrawalError(err));
  }
};

export const rejectWithdrawal = (withdrawalId) => async (dispatch, getState) => {
  dispatch(rejectWithdrawalRequest(withdrawalId));

  try {
    const currentUser = getState()?.user?.currentUser;
    const currentUserId = currentUser?.id?.uuid;

    if (!currentUserId) {
      throw new Error('User not authenticated');
    }

    const res = await adminRejectWithdrawal({
      requestId: withdrawalId,
      reviewedBy: currentUserId,
      currentUserId
    });
    const data = res?.data?.data || res?.data;
    const updated = data?.request || data?.item || data;
    dispatch(rejectWithdrawalSuccess(updated));
  } catch (error) {
    console.error('Error rejecting withdrawal:', error);
    const err = storableError(error);
    dispatch(rejectWithdrawalError(err));
  }
};
// ================ Page Data Loading ================ //
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  const currentUserId = currentUser?.id?.uuid;

  const queryParams = parse(search);
  const {  page=1, status="all" } = queryParams;

  // Map tab status to API status
  const mapStatus = s => {
    if (s === 'paid') return 'approved';
    if (s === 'cancelled') return 'rejected';
    if (s === 'all') return undefined;
    return s;
  };
  const apiStatus = mapStatus(status);

  if (!currentUserId) {
    console.log('No current user ID available for AdminWithdrawalsPage');
    return Promise.resolve();
  }

  return dispatch(fetchWithdrawals({page, perPage:10, status: mapStatus(status) })).catch(e => {
    console.error('Error loading AdminWithdrawalsPage data:', e);
    throw e;
  });
};